import { useState, useEffect, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';

export interface ObservatorySnapshot {
  activeSessions: any[];
  activeThreads: any[];
  functionMetrics: Record<string, { count: number; totalMs: number }>;
  connectedClients: number;
}

export interface ObservatoryEvent {
  type: string;
  data: any;
  timestamp: string;
}

/**
 * Hook to connect to AI Observatory WebSocket for real-time monitoring
 */
export function useAIObservatory(apiUrl: string) {
  const [connected, setConnected] = useState(false);
  const [snapshot, setSnapshot] = useState<ObservatorySnapshot | null>(null);
  const [events, setEvents] = useState<ObservatoryEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<Socket | null>(null);
  const maxEvents = 100; // Keep only last 100 events in memory

  // Connect to WebSocket
  useEffect(() => {
    const connectWithAuth = async () => {
      // Get auth token from global auth provider
      let authToken = '';
      try {
        if ((window as any).authProvider?.getIdToken) {
          authToken = await (window as any).authProvider.getIdToken();
          console.log('🔐 [Observatory] Got auth token for WebSocket');
        }
      } catch (error) {
        console.warn('⚠️ [Observatory] Could not get auth token:', error);
      }

      const socket = io(`${apiUrl}/ai-observatory`, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        auth: {
          token: authToken,
        },
        extraHeaders: authToken ? {
          'Authorization': `Bearer ${authToken}`,
        } : {},
      });

      socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Connected to AI Observatory');
      setConnected(true);
      setError(null);
    });

    socket.on('disconnect', () => {
      console.log('❌ Disconnected from AI Observatory');
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('Connection error:', err);
      setError(`Connection error: ${err.message}`);
    });

    // Initial snapshot
    socket.on('observatory:snapshot', (data: ObservatorySnapshot) => {
      console.log('📊 Received observatory snapshot', data);
      setSnapshot(data);
    });

    // Real-time event handlers
    socket.on('thread:created', (data) => {
      addEvent('thread:created', data);
    });

    socket.on('thread:updated', (data) => {
      addEvent('thread:updated', data);
    });

    socket.on('message:sent', (data) => {
      addEvent('message:sent', data);
    });

    socket.on('function:called', (data) => {
      addEvent('function:called', data);
    });

    socket.on('session:started', (data) => {
      addEvent('session:started', data);
    });

    socket.on('session:ended', (data) => {
      addEvent('session:ended', data);
    });

    socket.on('metric:updated', (data) => {
      addEvent('metric:updated', data);
    });

      return () => {
        socket.disconnect();
      };
    };

    connectWithAuth();
  }, [apiUrl]);

  const addEvent = useCallback((type: string, data: any) => {
    const event: ObservatoryEvent = {
      type,
      data,
      timestamp: data.timestamp || new Date().toISOString(),
    };

    setEvents((prev) => {
      const newEvents = [event, ...prev];
      return newEvents.slice(0, maxEvents);
    });
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  return {
    connected,
    snapshot,
    events,
    error,
    clearEvents,
  };
}

export interface ObservatoryStatistics {
  totalConfigurations: number;
  totalInstances: number;
  totalUsers: number;
  totalThreads: number;
  totalMessages: number;
  activeThreads: number;
  providerCounts: {
    tenantTypeProviders: number;
    applicationProviders: number;
    featureProviders: number;
  };
  functionCounts: {
    totalFunctions: number;
  };
  conversationStats: {
    averageMessagesPerThread: number;
    threadsToday: number;
    messagesToday: number;
  };
}

export interface AgentConfiguration {
  id: string;
  name: string;
  assistantKey: string;
  model?: string;
  applicationKey?: string;
  configuration?: {
    functions?: any[];
    instructions?: string;
    files?: any[];
  };
  reasoningStrategy?: string;
  metaData?: any;
}

export interface AgentInstance {
  id: string;
  name: string;
  externalId: string;
  assistantId?: string;
  userId: string;
  providerKey?: string;
  featureKey?: string;
  applicationKey?: string;
  metaData?: any;
}

export interface RegistryOverview {
  tenantTypeProviders: any[];
  applicationProviders: any[];
  featureProviders: any[];
  functionRegistry: {
    availableFunctions: string[];
    totalCount: number;
  };
}

export interface TopologyData {
  nodes: any[];
  edges: any[];
}

